import { AIChatSidebar } from '@components/ArtBoard/AIChat/components/AIChatSidebar/AIChatSidebar'
import { Publish } from '@components/ArtBoard/Publish'
import { SeoModeSidebar } from '@components/ArtBoard/SeoModeSidebar'
import SymbolNotificationBadge from '@components/ArtBoard/SettingsSidebar/components/SymbolLibrary/components/Symbols/components/Symbol/components/SymbolNotificationBadge/SymbolNotificationBadge'
import { StructureSidebar } from '@components/ArtBoard/StructureSidebar'
import {
  ShowResolvedContainer,
  ThreadsSidebar,
} from '@components/ArtBoard/ThreadsSidebar'
import { migrationArtBoardSelectors } from '@store/migrationSelectors/artBoardSelectors'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions, selectArtboardMode } from '@store/reducers/artboard'
import type { ArtBoardCursorMode } from '@store/reducers/artboard/types'
import { selectTagVisibility } from '@store/reducers/tags/visibility'
import type { AppState } from '@store/types'
import {
  selectSeoPanelState,
  selectThreadsPanelState,
} from '@storeOld/selectors'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import SystemDialogs from '@/components/App/SystemDialogs'
import { SettingsSidebar } from '@/components/ArtBoard/SettingsSidebar'
import {
  useArtboardMode,
  useGa4Callback,
  useIntervals,
  usePrintRender,
  useProjectAccess,
  useProjectModeReset,
  useProjectPermissions,
  useShowModalsBySearchParams,
  useSocket,
} from '@/hooks'
import { useAiGenerating } from '@/hooks/selectors/useAiGenerating'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import useKeyPressed from '@/hooks/useKeyPressed'
import { TOOLBAR_ICONS_PERMISSION } from '@/permissions/project/definitions'
import { clsJoin } from '@/utils'
import { keyboardKeys } from '@/utils/os/keys'
import Head from '../App/Head/Head'
import Modal from '../App/Modal/Modal'
import NotificationWrapper from '../App/NotificationContainer/NotificationWrapper'
import { AiWorkingFrame } from './AiWorkingFrame'
import { AiWorkingNotifier } from './AiWorkingNotifier'
import css from './ArtBoard.module.scss'
import Blocker from './Blocker/Blocker'
import Canvas from './Canvas/Canvas'
import DragAndDropOverlay from './DragAndDrop/DragAndDropOverlay'
import { Estimator } from './Estimator'
import { FileDropZone } from './FileDropZone'
import { Footer } from './Footer'
import { Header } from './Header'
import KeyboardListener from './KeyboardListener/KeyboardListener'
import { Preview } from './Preview'
import { ProjectToolbar } from './ProjectToolbar'
import Theme from './Theme/Theme'

// @TODO refactor old css file to css modules
import '../App/App.css'
import '../App/Global.scss'

const ArtBoard: React.FC = () => {
  // @TODO refactor this
  const body = useRef(document.body)

  useProjectPermissions()
  useProjectModeReset()
  useShowModalsBySearchParams()
  useGa4Callback()
  useIntervals()
  useSocket()

  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)

  const cursorMode = useSelector((state: AppState) => state.artboard.cursorMode)
  const previousModeRef = useRef<ArtBoardCursorMode | null>(null)

  useKeyPressed(keyboardKeys().space, (status) => {
    if (!canEdit) {
      return
    }

    if (status && cursorMode !== 'grab') {
      previousModeRef.current = cursorMode
      dispatch(artBoardActions.setCursorMode('grab'))

      return
    }

    if (previousModeRef.current) {
      dispatch(artBoardActions.setCursorMode(previousModeRef.current))
      previousModeRef.current = null
    }
  })

  const { isPrinting, isPdf, isThumbnail, isNotes } = usePrintRender()

  const mode = useSelector(selectArtboardMode)

  const {
    seoMode,
    publish,
    activityHistory,
    estimate,
    settings,
    storage,
    color,
    symbolLibrary,
  } = useArtboardMode()
  const sidebarMode =
    settings || activityHistory || storage || color || symbolLibrary

  const privateEstimate = useSelector(
    migrationProjectSelector.getProjectEstimateSettingPrivate
  )
  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const isEmbed = useSelector(migrationArtBoardSelectors.getIsEmbed)

  const { hasAccess: hasAccessPrivateEstimate } = useProjectAccess(
    TOOLBAR_ICONS_PERMISSION.ESTIMATE_PRIVATE
  )

  useEffect(() => {
    const prefix = '--artboard-mode'
    const classType = `${prefix}-${mode}`

    body.current.classList.forEach((classItem) => {
      if (classItem.includes(prefix)) {
        body.current.classList.remove(classItem)
      }
    })

    if (!body.current.classList.contains(classType)) {
      body.current.classList.add(classType)
    }

    return () => {}
  }, [mode])

  const disabled = useArtBoardDisabled()

  useEffect(() => {
    if (disabled) {
      document.body.classList.add('--artboard-disabled')
    } else {
      document.body.classList.remove('--artboard-disabled')
    }
  }, [disabled])

  // Dedicated flag for "the assistant is changing the project": on top of the
  // shared dim it makes the canvas non-editable, so the user can't edit in
  // parallel and race the sync (OCT-4826). Scoped to AI, unlike --artboard-disabled.
  const aiWorking = useAiGenerating()

  useEffect(() => {
    document.body.classList.toggle('--ai-working', aiWorking)
  }, [aiWorking])

  // Page-tag filter: the branch connector lines of the pages that are only kept
  // as a path to a match are dimmed along with the pages themselves (OCT-5007).
  // Used to be toggled from inside a Redux selector.
  const tagFilterIsActive = useSelector(
    (state: AppState) => selectTagVisibility(state).filterIsActive
  )

  useEffect(() => {
    document.body.classList.toggle('--opacity-node', tagFilterIsActive)

    return () => {
      document.body.classList.remove('--opacity-node')
    }
  }, [tagFilterIsActive])

  useEffect(() => {
    if (isPrinting) {
      body.current.classList.add('--print')

      if (isPdf) {
        body.current.classList.add('--pdf')
      }

      if (isThumbnail) {
        body.current.classList.add('--thumbnail')
      }

      if (isNotes) {
        body.current.classList.add('--sticky-notes')
      }
    }
  }, [])

  if (privateEstimate && !hasAccessPrivateEstimate) {
    if (estimate) {
      dispatch(artBoardActions.changeMode('edit'))
    }
  }

  const hasModal = useSelector(
    migrationProjectSelector.getProjectHasAccessBlocker
  )
  const showStructure = useSelector<AppState, boolean>(
    ({ artboard }) => artboard.showStructure
  )
  const showThreads = useSelector(selectThreadsPanelState)
  const showSeoPanel = useSelector(selectSeoPanelState)
  const isTemplate = useSelector(migrationProjectSelector.getProjectIsTemplate)

  const showSeoSidebar = seoMode && showSeoPanel
  const hideForTemplate = isTemplate && isReadOnly

  const showToolbar = !isPrinting && !isEmbed && !hasModal && !hideForTemplate

  return (
    <ShowResolvedContainer>
      <div className={clsJoin(css.app, seoMode && css.app_seo_mode)}>
        <Head />
        <Theme />
        {!seoMode && showThreads && <ThreadsSidebar />}
        {showSeoSidebar && <SeoModeSidebar />}
        <FileDropZone disabled={seoMode || disabled}>
          <div className={css.appCanvas}>
            <NotificationWrapper isPositionAbsolute />
            <Header />
            {showToolbar && <ProjectToolbar />}
            <Canvas />

            {!isPrinting && publish && <Publish />}

            {!isPrinting && !activityHistory && (
              <Footer
                isReadOnly={isReadOnly}
                isEmbed={isEmbed}
                hideTabs={hideForTemplate}
              />
            )}
          </div>
        </FileDropZone>
        <Preview />

        {!isPrinting && !isEmbed && !seoMode && showStructure && (
          <StructureSidebar />
        )}

        {!isPrinting && estimate && <Estimator />}

        {!isPrinting && <SymbolNotificationBadge />}

        <AIChatSidebar />

        <AiWorkingFrame />
        <AiWorkingNotifier />

        {!isPrinting && sidebarMode && <SettingsSidebar />}

        {!isPrinting && (
          <>
            <Modal />
            <SystemDialogs />
            <Blocker />
          </>
        )}

        <KeyboardListener />

        {!isReadOnly && <DragAndDropOverlay />}
      </div>
    </ShowResolvedContainer>
  )
}

export default ArtBoard
